// @ts-check
import React from 'react';
import { Hint, Input, Autocomplete, Dropdown , cn} from "@artprov/design-system";

/**
 * @typedef {Object} FieldProps
 * @property {string} [label] - Etiqueta para el campo.
 * @property {string} [name] - Nombre del campo, usado en formularios.
 * @property {string} [placeholder] - Texto de marcador de posición.
 * @property {boolean} [isRequired] - Indica si el campo es obligatorio.
 * @property {boolean} [isOptional] - Indica si el campo es opcional.
 * @property {string} [info] - Información adicional del campo.
 * @property {Object} [control] - Control del formulario (pasado desde react-hook-form u otro).
 * @property {Object} [errors] - Objeto de errores del formulario.
 * @property {React.ElementType} [Component] - Componente interno que se renderiza (Input, Dropdown, Autocomplete, etc.).
 * @property {Array<{ value: string; label: string } | { id: string; descripcion: string }>} [options] - Opciones para el componente.
 * @property {string} [variant] - Variante específica del Dropdown.
 * @property {string} [textButton] - Texto que se muestra en el Dropdown (solo se usa si el componente es Dropdown).
 * @property {string} [searchValue] - Valor de búsqueda para Autocomplete.
 * @property {(option: any) => void} [setSelected] - Callback para establecer la opción seleccionada en Autocomplete.
 * @property {(value: string) => void} [setSearchValue] - Callback para establecer el valor de búsqueda en Autocomplete.
 * @property {boolean} [disabled] - Indica si el campo está deshabilitado.
 * @property {boolean} [cargando] - Indica si Autocomplete está cargando.
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - Evento `onChange` que se ejecuta al cambiar el valor del campo.
 * @property {string} [className] - The class name for the input element of the Field component.
 * @property {Object} [props] - Props adicionales para pasar al componente interno.
 */

/**
 * Componente Field reutilizable
 * @param {FieldProps} props - Props del componente.
 */
const Field = ({
    label = '',
    name = '',
    placeholder = 'Placeholder',
    isRequired = false,
    isOptional = false,
    info = '',
    control = {},
    errors = {},
    Component = Input,
    options = [],
    variant = 'outline',
    textButton = '',
    searchValue = '',
    setSelected = () => {},
    setSearchValue = () => {},
    disabled = false,
    cargando = false,
    onChange = () => {},
    className = '',
    ...props
}) => {
  const errorMessage = errors[name] ? errors[name]?.message : null;
  const isError = !!errorMessage;

  const isDropdown = Component === Dropdown && variant === 'outline';
  const isAutocomplete = Component === Autocomplete;

  const handleChange = (value, ...rest) => {
    const event = {
      target: {
        name,
        value,
        form: props.form || null,
      },
      preventDefault: () => {},
      ...rest,
    };
    onChange(event);
  };

  return (
    <div className="mb-1">
      <Component
        hints={[]}
        name={name}
        label={label}
        placeholder={placeholder}
        isRequired={isRequired}
        isOptional={isOptional}
        control={control}
        errors={errors}
        options={options}
        variant={variant}
        disabled={disabled}
        onChange={(e) => handleChange(e.target?.value ?? e, e)}
        {...(isDropdown && { textButton })}
        {...(isAutocomplete && {
          searchValue,
          setSelected,
          setSearchValue,
          cargando,
        })}
        {...props}
        // className={`mb-1`}
        className={cn(
          'mb-1',
          className
        )}
      />
      {isError && (
        <Hint text={errorMessage} status="error" className="mt-1" />
      )}
    </div>
  );
};

export default Field;